import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { 
    Calendar, MapPin, Phone, User, Truck, Receipt, CreditCard, 
    AlertCircle, ArrowLeft, RefreshCw, Drill, CheckCircle, 
    Clock, TrendingUp, DollarSign, FileText, Plus, Eye, 
    Activity, Target, Wallet, AlertTriangle
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchAgentById } from '../../../services/api';

import AgentDrillingForm from './Forms/AgentDrillingForm';


// Loading skeleton component
const LoadingSkeleton = ({ className = "", height = "h-4" }) => (
    <div className={`animate-pulse bg-gray-200 rounded ${height} ${className}`}></div>
);

// Card skeleton for summary cards
const SummaryCardSkeleton = () => (
    <div className="bg-white rounded-lg shadow-md p-4">
        <LoadingSkeleton height="h-8" className="w-16 mb-2" />
        <LoadingSkeleton height="h-4" className="w-24" />
    </div>
);

// Error component
const ErrorDisplay = ({ error, onRetry }) => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Data</h2>
        <p className="text-red-600 mb-4">{error?.message || 'An unexpected error occurred'}</p>
        <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
            <RefreshCw className="w-4 h-4" />
            Retry
        </button>
    </div>
);

// Status badge component
const StatusBadge = ({ status, amount }) => {
    const getStatusConfig = () => {
        if (amount > 50000) return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', label: 'High Priority' };
        if (amount > 20000) return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', label: 'Medium Priority' };
        return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', label: 'Low Priority' };
    };
    
    const config = getStatusConfig();
    return (
        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
            <div className={`w-2 h-2 rounded-full ${config.text.includes('red') ? 'bg-red-500' : config.text.includes('yellow') ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
            {config.label}
        </span>
    );
};

// Customer type badge
const CustomerTypeBadge = ({ type }) => {
    const config = type === 'commercial' 
        ? { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: '🏢' }
        : { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', icon: '🏠' };
    
    return (
        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${config.bg} ${config.text} ${config.border}`}>
            <span>{config.icon}</span>
            {type?.charAt(0).toUpperCase() + type?.slice(1) || 'N/A'}
        </span>
    );
};

const AgentDetailsComponent = () => {
    const { agentId } = useParams();
    const navigate = useNavigate(); // Add this line
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

     const [showDrillingForm, setShowDrillingForm] = useState(false);
    const [selectedAgentForAction, setSelectedAgentForAction] = useState(null);
    const [selectedBillForDetails, setSelectedBillForDetails] = useState(null);
    const [showBillDetails, setShowBillDetails] = useState(false);
    

    // Memoized formatters to prevent recreation on every render
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    }, []);

    const formatCurrency = useCallback((amount) => {
        if (amount == null || isNaN(amount)) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    }, []);

    // Load agent data function
    const loadAgentData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const agentData = await fetchAgentById(agentId);
            setData(agentData);
        } catch (err) {
            console.error('Error loading agent data:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [agentId]);

    useEffect(() => {
        if (agentId) {
            loadAgentData();
        }
    }, [agentId, loadAgentData]);

    // Memoized computed values
    const summaryData = useMemo(() => {
        if (!data?.summary) return null;
        return {
            totalDrillingPoints: data.summary.totalDrillingPoints || 0,
            totalBillAmount: data.summary.totalBillAmount || 0,
            totalPaidAmount: data.summary.totalPaidAmount || 0,
            pendingAmount: data.summary.pendingAmount || 0
        };
    }, [data?.summary]);

    // Early return for loading state
    if (loading) {
        return (
            <div className=" mx-auto p-6 bg-gray-50 min-h-screen">
                <div className="space-y-6">
                    {/* Header Skeleton */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <LoadingSkeleton className="w-6 h-6 rounded" />
                            <LoadingSkeleton height="h-7" className="w-48" />
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="flex items-center gap-2">
                                    <LoadingSkeleton className="w-4 h-4 rounded" />
                                    <LoadingSkeleton className="w-32" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Summary Cards Skeleton */}
                    <div className="grid md:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <SummaryCardSkeleton key={i} />)}
                    </div>

                    {/* Content Skeleton */}
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <LoadingSkeleton height="h-6" className="w-40 mb-4" />
                        <div className="space-y-4">
                            {[1, 2].map(i => (
                                <div key={i} className="border rounded-lg p-4 bg-gray-50">
                                    <LoadingSkeleton height="h-20" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Early return for error state
    if (error) {
        return (
            <div className=" mx-auto p-6 bg-gray-50 min-h-screen">
                <ErrorDisplay error={error} onRetry={loadAgentData} />
            </div>
        );
    }

    // Early return if no data
    if (!data) {
        return (
            <div className=" mx-auto p-6 bg-gray-50 min-h-screen">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-lg font-semibold text-yellow-800">No Data Found</h2>
                    <p className="text-yellow-600">No agent data available for ID: {agentId}</p>
                </div>
            </div>
        );
    }

    const handleAddDrillingData = (agent) => {
        setSelectedAgentForAction(agent);
        setShowDrillingForm(true);
    };

    const handleViewBillDetails = (bill) => {
        setSelectedBillForDetails(bill);
        setShowBillDetails(true);
    };

    const closeBillDetails = () => {
        setShowBillDetails(false);
        setSelectedBillForDetails(null);
    };

    return (
         <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
            <div className="max-w-7xl mx-auto p-6 space-y-8">

                {/* Header - Agent Info */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
                        <div className="flex items-center gap-4 mb-4">
                            <button
                                onClick={() => window.history.back()}
                                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all duration-200 backdrop-blur-sm"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span className="font-medium">Back</span>
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-3 rounded-xl">
                                    <User className="w-8 h-8" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold">{data.name || 'N/A'}</h1>
                                    <p className="text-blue-100 text-lg">Agent Profile</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-6">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                <div className="bg-green-100 p-3 rounded-xl">
                                    <Phone className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Phone Number</p>
                                    <p className="text-lg font-semibold text-gray-800">{data.number || 'N/A'}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                                <div className="bg-purple-100 p-3 rounded-xl">
                                    <MapPin className="w-6 h-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Address</p>
                                    <p className="text-lg font-semibold text-gray-800">{data.address || 'N/A'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Button */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                    <button
                        onClick={() => handleAddDrillingData(data)}
                        className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-4 rounded-xl hover:from-orange-600 hover:to-red-600 flex items-center justify-center gap-3 transition-all duration-200 transform hover:scale-[1.02] shadow-lg font-semibold text-lg"
                    >
                        <Drill className="w-6 h-6" />
                        <span>Add New Drilling Point</span>
                        <Plus className="w-5 h-5" />
                    </button>
                </div>

                {/* Summary Cards */}
                {summaryData && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-blue-100 p-3 rounded-xl">
                                    <Target className="w-6 h-6 text-blue-600" />
                                </div>
                                <TrendingUp className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="text-3xl font-bold text-blue-600 mb-1">{summaryData.totalDrillingPoints}</div>
                            <div className="text-sm text-gray-600 font-medium">Total Drilling Points</div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-green-100 p-3 rounded-xl">
                                    <DollarSign className="w-6 h-6 text-green-600" />
                                </div>
                                <Activity className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="text-3xl font-bold text-green-600 mb-1">{formatCurrency(summaryData.totalBillAmount)}</div>
                            <div className="text-sm text-gray-600 font-medium">Total Bill Amount</div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-emerald-100 p-3 rounded-xl">
                                    <Wallet className="w-6 h-6 text-emerald-600" />
                                </div>
                                <CheckCircle className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div className="text-3xl font-bold text-emerald-600 mb-1">{formatCurrency(summaryData.totalPaidAmount)}</div>
                            <div className="text-sm text-gray-600 font-medium">Amount Received</div>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                            <div className="flex items-center justify-between mb-4">
                                <div className="bg-red-100 p-3 rounded-xl">
                                    <AlertTriangle className="w-6 h-6 text-red-600" />
                                </div>
                                <StatusBadge amount={summaryData.pendingAmount} />
                            </div>
                            <div className="text-3xl font-bold text-red-600 mb-1">{formatCurrency(summaryData.pendingAmount)}</div>
                            <div className="text-sm text-gray-600 font-medium">Pending Amount</div>
                        </div>
                    </div>
                )}

                {/* Drilling Points */}
                {data.DrillingPoint && data.DrillingPoint.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-6 text-white">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/20 p-3 rounded-xl">
                                    <Truck className="w-8 h-8" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">Drilling Points</h2>
                                    <p className="text-green-100 text-lg">{data.DrillingPoint.length} Active Locations</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-6 space-y-6">
                            {data.DrillingPoint.map((point, index) => (
                                <div key={point._id || index} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300">
                                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-4">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-5 h-5 text-blue-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Date</p>
                                                <p className="font-semibold text-gray-800">{formatDate(point.date)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Truck className="w-5 h-5 text-green-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Vehicle</p>
                                                <p className="font-semibold text-gray-800">{point.vehicleName || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500 font-medium mb-1">Customer Type</p>
                                            <CustomerTypeBadge type={point.typeOfCustomer} />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Phone className="w-5 h-5 text-purple-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Mobile</p>
                                                <p className="font-semibold text-gray-800">{point.mobileNumber || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div className="flex items-center gap-3">
                                            <User className="w-5 h-5 text-indigo-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Client Name</p>
                                                <p className="font-semibold text-gray-800 text-lg">{point.name || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <MapPin className="w-5 h-5 text-red-500" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Address</p>
                                                <p className="font-semibold text-gray-800 whitespace-pre-line">{point.address || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bills - Simplified View */}
                {data.Bill && data.Bill.length > 0 && (
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 text-white">
                            <div className="flex items-center gap-4">
                                <div className="bg-white/20 p-3 rounded-xl">
                                    <Receipt className="w-8 h-8" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">Bills & Invoices</h2>
                                    <p className="text-purple-100 text-lg">{data.Bill.length} Total Bills</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="p-6">
                            <div className="grid gap-4">
                                {data.Bill.map((bill, index) => (
                                    <div key={bill._id || index} className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 grid md:grid-cols-4 gap-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-indigo-100 p-2 rounded-lg">
                                                        <User className="w-5 h-5 text-indigo-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Client</p>
                                                        <p className="font-bold text-gray-800">{bill.clientName || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-purple-100 p-2 rounded-lg">
                                                        <Calendar className="w-5 h-5 text-purple-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Date</p>
                                                        <p className="font-semibold text-gray-800">{formatDate(bill.date)}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-green-100 p-2 rounded-lg">
                                                        <DollarSign className="w-5 h-5 text-green-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Total Amount</p>
                                                        <p className="font-bold text-green-600 text-lg">{formatCurrency(bill.totalBillAmount)}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-blue-100 p-2 rounded-lg">
                                                        <Drill className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-gray-500 font-medium">Drill Feet</p>
                                                        <p className="font-semibold text-gray-800">{bill.drillFeet || 0} ft</p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="ml-6">
                                                <button
                                                    onClick={() => handleViewBillDetails(bill)}
                                                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 transform hover:scale-105 shadow-lg font-semibold"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Quick Info Bar */}
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <div className="flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-gray-600">
                                                        <span className="font-medium">Additional Items:</span> {bill.additional?.length || 0}
                                                    </span>
                                                    <span className="text-gray-600">
                                                        <span className="font-medium">Payments:</span> {bill.Payment?.length || 0}
                                                    </span>
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono">
                                                    ID: {bill._id || 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {/* Drilling Form Modal Placeholder */}
            {showDrillingForm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Add Drilling Point</h3>
                        <p className="text-gray-600">Drilling form would be displayed here for agent: {selectedAgentForAction}</p>
                        <button 
                            onClick={() => setShowDrillingForm(false)}
                            className="mt-6 px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Bill Details Modal */}
            {showBillDetails && selectedBillForDetails && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="bg-white/20 p-3 rounded-xl">
                                        <Receipt className="w-8 h-8" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold">Bill Details</h3>
                                        <p className="text-purple-100">Complete invoice information</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={closeBillDetails}
                                    className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                                >
                                    <ArrowLeft className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div className="p-6 max-h-[calc(90vh-140px)] overflow-y-auto">
                            <div className="space-y-6">
                                
                                {/* Bill Header Info */}
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <div className="flex items-center gap-3">
                                            <User className="w-6 h-6 text-indigo-600" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Client Name</p>
                                                <p className="font-bold text-gray-800 text-xl">{selectedBillForDetails.clientName || 'N/A'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-6 h-6 text-purple-600" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Bill Date</p>
                                                <p className="font-bold text-gray-800 text-xl">{formatDate(selectedBillForDetails.date)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-6 h-6 text-pink-600" />
                                            <div>
                                                <p className="text-sm text-gray-500 font-medium">Bill ID</p>
                                                <p className="font-mono text-sm bg-gray-100 px-3 py-1 rounded-lg inline-block">{selectedBillForDetails._id || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Drilling Details */}
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                                    <div className="flex items-center gap-3 mb-6">
                                        <Drill className="w-6 h-6 text-green-600" />
                                        <h4 className="text-xl font-bold text-gray-800">Drilling Details</h4>
                                    </div>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                            <p className="text-sm text-gray-500 font-medium mb-1">Drill Feet</p>
                                            <p className="text-2xl font-bold text-blue-600">{selectedBillForDetails.drillFeet || 0}</p>
                                            <p className="text-xs text-gray-400">feet</p>
                                        </div>
                                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                            <p className="text-sm text-gray-500 font-medium mb-1">Rate per Feet</p>
                                            <p className="text-2xl font-bold text-purple-600">{formatCurrency(selectedBillForDetails.drillRate)}</p>
                                            <p className="text-xs text-gray-400">per feet</p>
                                        </div>
                                        <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                            <p className="text-sm text-gray-500 font-medium mb-1">Drill Amount</p>
                                            <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedBillForDetails.totalDrillAmount)}</p>
                                            <p className="text-xs text-gray-400">total</p>
                                        </div>
                                        <div className="text-center p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg shadow-sm border-2 border-green-300">
                                            <p className="text-sm text-gray-500 font-medium mb-1">Total Bill</p>
                                            <p className="text-3xl font-bold text-green-700">{formatCurrency(selectedBillForDetails.totalBillAmount)}</p>
                                            <p className="text-xs text-green-600 font-semibold">GRAND TOTAL</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Items */}
                                {selectedBillForDetails.additional && selectedBillForDetails.additional.length > 0 && (
                                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                                        <div className="flex items-center gap-3 mb-4">
                                            <Plus className="w-6 h-6 text-blue-600" />
                                            <h4 className="text-xl font-bold text-gray-800">Additional Items ({selectedBillForDetails.additional.length})</h4>
                                        </div>
                                        <div className="bg-white rounded-lg overflow-hidden shadow-sm">
                                            <table className="w-full">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="text-left py-4 px-6 font-semibold text-gray-700">Item</th>
                                                        <th className="text-center py-4 px-6 font-semibold text-gray-700">Quantity</th>
                                                        <th className="text-center py-4 px-6 font-semibold text-gray-700">Rate</th>
                                                        <th className="text-right py-4 px-6 font-semibold text-gray-700">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedBillForDetails.additional.map((item, itemIndex) => (
                                                        <tr key={item._id || itemIndex} className="border-t border-gray-100 hover:bg-gray-50">
                                                            <td className="py-4 px-6 font-medium text-gray-800">{item.name || 'N/A'}</td>
                                                            <td className="text-center py-4 px-6 text-gray-700">{item.quantity || 0}</td>
                                                            <td className="text-center py-4 px-6 text-gray-700">{formatCurrency(item.rate)}</td>
                                                            <td className="text-right py-4 px-6 font-bold text-green-600">{formatCurrency(item.total)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Payment History */}
                                {selectedBillForDetails.Payment && selectedBillForDetails.Payment.length > 0 && (
                                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                                        <div className="flex items-center gap-3 mb-4">
                                            <CreditCard className="w-6 h-6 text-indigo-600" />
                                            <h4 className="text-xl font-bold text-gray-800">Payment History ({selectedBillForDetails.Payment.length})</h4>
                                        </div>
                                        <div className="space-y-4">
                                            {selectedBillForDetails.Payment.map((payment, paymentIndex) => (
                                                <div key={payment._id || paymentIndex} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200">
                                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-green-100 p-2 rounded-lg">
                                                                <DollarSign className="w-4 h-4 text-green-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 font-medium">Amount</p>
                                                                <p className="text-lg font-bold text-green-600">{formatCurrency(payment.amount)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-blue-100 p-2 rounded-lg">
                                                                <CreditCard className="w-4 h-4 text-blue-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 font-medium">Method</p>
                                                                <p className="font-semibold text-gray-800 capitalize">{payment.paymentMethod || 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-purple-100 p-2 rounded-lg">
                                                                <Calendar className="w-4 h-4 text-purple-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 font-medium">Date</p>
                                                                <p className="font-semibold text-gray-800">{formatDate(payment.paymentDate)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <div className="bg-orange-100 p-2 rounded-lg">
                                                                <FileText className="w-4 h-4 text-orange-600" />
                                                            </div>
                                                            <div>
                                                                <p className="text-sm text-gray-500 font-medium">Notes</p>
                                                                <p className="font-semibold text-gray-800">{payment.notes || 'No notes'}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                                    <button 
                                        onClick={closeBillDetails}
                                        className="px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors font-semibold"
                                    >
                                        Close
                                    </button>
                                    <button 
                                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-semibold"
                                    >
                                        Print Invoice
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
            `}</style>
        </div>
    );
};

export default AgentDetailsComponent;